import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuthStore } from '../stores/authStore';
import { useNavigate } from 'react-router-dom';

const loginSchema = z.object({
  username: z.string().min(1, 'ユーザー名を入力してください'),
  password: z.string().min(1, 'パスワードを入力してください'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginPage: React.FC = () => {
  const { login, isLoading, error, isAuthenticated, user, clearError } = useAuthStore();
  const navigate = useNavigate();
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkLoading, setMagicLinkLoading] = useState(false);
  const [magicLinkError, setMagicLinkError] = useState('');
  const [emailForMagicLink, setEmailForMagicLink] = useState('');

  // 前回のメールアドレスをlocalStorageから読み込み
  useEffect(() => {
    const savedEmail = localStorage.getItem('lastLoginEmail');
    if (savedEmail) {
      setEmailForMagicLink(savedEmail);
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  useEffect(() => {
    if (isAuthenticated && user) {
      // service_operatorはダッシュボードへ、それ以外はボトル一覧へ
      if (user.role === 'service_operator') {
        navigate('/service/dashboard');
      } else {
        navigate('/bottles');
      }
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    clearError();
  }, [clearError]);

  const onSubmit = async (data: LoginFormData) => {
    await login(data.username, data.password);
  };

  const handleSendMagicLink = async () => {
    if (!emailForMagicLink) {
      setMagicLinkError('メールアドレスを入力してください');
      return;
    }

    setMagicLinkLoading(true);
    setMagicLinkError('');

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailForMagicLink }),
      });

      if (response.ok) {
        localStorage.setItem('lastLoginEmail', emailForMagicLink);
        setMagicLinkSent(true);
      } else {
        const errorData = await response.json();
        setMagicLinkError(errorData.error || 'マジックリンクの送信に失敗しました');
      }
    } catch {
      setMagicLinkError('ネットワークエラーが発生しました');
    } finally {
      setMagicLinkLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-4xl font-bold text-gray-900 mb-2">
          BottleMan
        </h1>
        <h2 className="text-center text-2xl font-medium text-gray-600">
          ログイン
        </h2>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                ユーザー名
              </label>
              <div className="mt-1">
                <input
                  {...register('username')}
                  type="text"
                  autoComplete="username"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="ユーザー名を入力"
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-600">{errors.username.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                パスワード
              </label>
              <div className="mt-1">
                <input
                  {...register('password')}
                  type="password"
                  autoComplete="current-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="パスワードを入力"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'ログイン中...' : 'ログイン'}
              </button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                パスワードを忘れた方はこちら
              </button>
            </div>
          </form>

          {/* マジックリンクセクション */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-center text-sm text-gray-600 mb-4">または</p>
            
            {magicLinkSent ? (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-sm text-green-600 text-center">
                  {emailForMagicLink} にマジックリンクを送信しました。<br />
                  メール内のリンクをクリックしてサインインしてください。
                </p>
              </div>
            ) : (
              <div>
                <label htmlFor="magic-link-email" className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレスでサインイン
                </label>
                <div className="flex gap-2">
                  <input
                    id="magic-link-email"
                    type="email"
                    value={emailForMagicLink}
                    onChange={(e) => setEmailForMagicLink(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="email@example.com"
                  />
                  <button
                    type="button"
                    onClick={handleSendMagicLink}
                    disabled={magicLinkLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {magicLinkLoading ? '送信中...' : '送信'}
                  </button>
                </div>
                {magicLinkError && (
                  <p className="mt-2 text-sm text-red-600">{magicLinkError}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
